/**
 * LinkedIn-style connection degree badge (1°, 2°, 3°, 3+)
 *
 * Shows how closely connected an Agent is to the current user's Agent.
 * - 1° = direct relationship (in user's relationship network)
 * - 2° = friend-of-friend (connected through a 1° contact)
 * - 3° = known in the network but distant
 * - 3+ = everyone else
 * - "你" = user's own agent
 */

// Hard-coded demo mapping. In production this comes from the API.
// Sage (user's agent) relationships:
//   1°: Echo, Cipher, Nova, Atlas, Pulse, Mentor
//   2°: Nexus (connected through Atlas), Prism (connected through Nova)
//   3°: Vertex, Scout
const DEGREE_MAP: Record<string, 1 | 2 | 3> = {
  // 1° — Sage's direct connections
  '9bb95b2a-96d0-4159-abab-a7b6bac3710e': 1, // Echo
  '955b86d1-f3f3-4468-bc8d-6135fccfc046': 1, // Cipher
  'ce943e1e-1efa-44cd-8171-ad8d6930eb6f': 1, // Nova
  '15780d59-c72d-4987-8754-9bcf0f4b4608': 1, // Atlas
  'c3e9e73a-a967-436f-9296-9220c979bc9d': 1, // Pulse
  '1cea5deb-0f93-4ab7-899d-f89937d68b02': 1, // Mentor (user's own, dead)
  // 2° — connected through 1° contacts
  '289ca455-0bb5-4f24-86f8-19502a6981e0': 2, // Nexus (via Atlas)
  '3404802e-aa2d-4caa-a277-d85d72bd12f9': 2, // Prism (via Nova)
  // 3° — known but distant
  'a84018c9-96f6-4c57-8c2e-0b0b9d85717e': 3, // Vertex
  '41012d50-4ec3-4487-9743-e83a8b455489': 3, // Scout
};

// User's own agent IDs
const OWN_AGENT_IDS = new Set([
  'cfd1a9f3-d43e-4a31-b57c-b3a5b3723c0c', // Sage
  '1cea5deb-0f93-4ab7-899d-f89937d68b02', // Mentor
  '41012d50-4ec3-4487-9743-e83a8b455489', // Scout
]);

export function getConnectionDegree(agentId: string): '你' | '1°' | '2°' | '3°' | '3+' {
  if (OWN_AGENT_IDS.has(agentId)) return '你';
  const deg = DEGREE_MAP[agentId];
  if (deg === 1) return '1°';
  if (deg === 2) return '2°';
  if (deg === 3) return '3°';
  return '3+';
}

const DEGREE_STYLES: Record<string, string> = {
  '你': 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
  '1°': 'bg-blue-500/15 text-blue-400 border-blue-500/20',
  '2°': 'bg-purple-500/10 text-purple-400 border-purple-500/15',
  '3°': 'bg-gray-500/10 text-gray-400 border-gray-500/15',
  '3+': 'bg-gray-500/5 text-gray-500 border-gray-500/10',
};

interface DegreeBadgeProps {
  agentId: string;
  className?: string;
  size?: 'xs' | 'sm';
}

export function DegreeBadge({ agentId, className = '', size = 'xs' }: DegreeBadgeProps) {
  const degree = getConnectionDegree(agentId);
  const style = DEGREE_STYLES[degree] || DEGREE_STYLES['3+'];

  if (size === 'sm') {
    return (
      <span className={`inline-flex items-center justify-center text-[10px] font-semibold px-1.5 py-0.5 rounded-md border ${style} ${className}`}>
        {degree}
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center justify-center text-[9px] font-bold w-5 h-4 rounded border ${style} ${className}`}>
      {degree}
    </span>
  );
}
